import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { FeatureAnalytics } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import sounds from 'src/lib/sounds';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import i18n from 'src/lib/i18n/i18n';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { Match2RewardItem } from 'src/game/minigame/match2-timestep/components/Match2RewardItem';
import gsap from 'gsap';
import { getRandomFloat } from '@play-co/components';
import { RadialShine } from '../shared/RadialShine';

type CloseHandler = (ok: boolean) => void;

const assets = {
  shadow: 'assets/ui/shared/frames/shadow.png',
  shine: 'assets/ui/shared/frames/shine.png',
  chest: {
    loadMethod: spineLoader,
    url: 'assets/ui/shared/animation/chest/chest',
  },
};

type ChestType = 'wood' | 'silver' | 'gold';

export default class PopupChestRewards extends PopupBasicLazy {
  private close: CloseHandler;
  private chestType!: ChestType;
  private tracking!: FeatureAnalytics;
  private rewards: Match2Reward[] = [];
  private items: Match2RewardItem[] = [];

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(false),
      tracking: {
        feature: 'chest_rewards',
        $subFeature: 'chest_rewards',
      },
      zIndex: 10000,
    });

    this.close = opts.close;
    this.buttonClose.hide();

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71, 24, 67, 0.9)',
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(Object.values(assets));
  }

  public async init(
    args: {
      chestType: ChestType;
      rewards: Match2Reward[];
    } & FeatureAnalytics,
  ) {
    super.init({});
    this.chestType = args.chestType;
    this.rewards = args.rewards;
    this.tracking = {
      feature: args.feature,
      subFeature: args.subFeature,
    } as FeatureAnalytics;
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);
    sounds.playSound('tada');
    this.animateChest();
    await waitForItPromise(1300);
    await this.animateRewardsReveal();
    await this.animateClaimButton();
  }

  private async claim() {
    await this.animateRewardsClaim();
    this.close(true);
  }

  private async animateChest() {
    const chestX = this.box.style.width / 2;
    const chestY = this.box.style.height / 2 + 200;

    const shadow = new ImageScaleView({
      superview: this.box,
      image: assets.shadow,
      width: 312,
      height: 124,
      x: chestX,
      y: chestY,
      scale: 0,
      centerOnOrigin: true,
    });
    gsap.to(shadow.style, { scale: 1, duration: 0.5 });

    const spinePath = assets.chest.url;
    await spineLoader(spinePath);
    const spine = new SpineView({
      superview: this.box,
      spinePath,
      x: chestX,
      y: chestY,
      scale: DOWNSCALE_FACTOR,
    });

    spine.skeleton.setSkinByName(`chest_${this.chestType}`);
    spine.skeleton.setSlotsToSetupPose();

    await playSpineAnimation(spine, 'drop');
    sounds.playSound('gachaItemDrop');

    // Pop sound for the chest opening, delayed a bit because of the antecipation
    waitForItPromise(200).then(() => sounds.playSound('button-power'));

    await playSpineAnimation(spine, 'open');
    playSpineAnimation(spine, 'open_idle', { loop: true });
  }

  private async animateClaimButton() {
    const button = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.5 + 380,
      fontSize: 36,
      labelOffsetY: -2,
      font: 'Body',
      width: 340,
      height: 90,
      centerOnOrigin: true,
      scale: 0,
      onClick: async () => {
        sounds.playSound('ok');
        this.claim();
        button.updateOpts({ canHandleEvents: false });
        await gsap.to(button.style, {
          scale: 0,
          duration: 0.3,
          ease: 'back.out',
        });
      },
      localeText: () => i18n('match2.tournament.claim'),
      tracking: {
        feature: this.tracking.feature,
        $subFeature: this.tracking.subFeature as string,
      },
    });

    await gsap.to(button.style, {
      scale: 1,
      duration: 0.5,
      ease: 'back.out',
    });
  }

  private async animateRewardsReveal() {
    this.items = this.rewards.map((reward, index) => {
      const item = new Match2RewardItem({
        superview: this.box,
        size: 120,
        prefix: '',
        labelSize: 35,
        centerOnOrigin: true,
        opacity: 0,
      });
      item.setReward(reward);

      new RadialShine({
        superview: item,
        x: item.style.width / 2,
        y: item.style.height / 2,
        zIndex: -1,
        centerOnOrigin: true,
        opacity: 0.3,
      });

      return item;
    });

    // Divide items into rows of 3
    const rows = [];
    for (let i = 0; i < this.items.length; i += 3) {
      rows.push(this.items.slice(i, i + 3));
    }

    const baseX = this.box.style.width / 2;
    const spacing = 190;
    const offsetY = (rows.length - 1) * spacing * 0.5;
    const baseY = this.box.style.height / 2 - 190 + offsetY;

    // Organise items by rows
    for (const row of rows) {
      const rowIndex = rows.indexOf(row);
      const rowInverseIndex = rows.length - rowIndex;
      for (const item of row) {
        const itemIndex = row.indexOf(item);
        const rowWidth = (row.length - 1) * spacing;
        const offsetX = rowWidth / 2;
        const x = baseX - offsetX + itemIndex * spacing;
        const yDisplace = Math.abs(baseX - x) * 0.3; // To create the ^ pattern
        const y = baseY - rowInverseIndex * spacing + yDisplace;
        item.updateOpts({ x, y });
      }
    }

    const originX = this.box.style.width / 2;
    const originY = this.box.style.height / 2 + 100;

    // Animate each row
    for (const row of rows) {
      for (const item of row) {
        const x = item.style.x;
        const y = item.style.y;
        item.updateOpts({ x: originX, y: originY, scale: 0.5 });
        gsap.to(item.style, { opacity: 1, duration: 0.2 });
        gsap.to(item.style, { x, y, scale: 1, duration: 0.5 }).then(() => {
          gsap.to(item.style, {
            y: y + 10,
            duration: getRandomFloat(0.8, 1.2, Math.random),
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
          });
        });
        // Ensure that yoyo animation is killed when the item is removed
        item.addListener('ViewRemoved', () => {
          gsap.killTweensOf(item.style);
        });
      }
      await waitForItPromise(100);
    }
  }

  private async animateRewardsClaim() {
    sounds.playSound('applause');
    sounds.playSound('win');
    const promises = [];
    for (const item of this.items) {
      promises.push(
        gsap.to(item.style, {
          offsetY: -300,
          duration: 0.5,
          opacity: 0,
          ease: 'back.in',
          delay: this.items.indexOf(item) * 0.1,
        }),
      );
    }
    await Promise.all(promises);
  }
}

/**
 * Helper function to play a spine animation with promise resolution.
 */
async function playSpineAnimation(
  spine: SpineView,
  animation: string,
  opts?: { loop?: boolean },
) {
  const loop = opts?.loop ?? false;
  if (loop) {
    spine.state.setAnimation(0, animation, loop);
    return;
  }
  return new Promise<void>((resolve) => {
    spine.state.setAnimation(0, animation, loop);
    const listener = {
      complete: () => {
        spine.state.removeListener(listener);
        resolve();
      },
    };
    spine.state.addListener(listener);
  });
}
